/**
 * Development configuration.
 *
 * `VITE_API_BASE_URL` overrides the default for pointing at a deployed stage.
 * Vite inlines this at build time, so it must never hold a secret — it is
 * visible in the shipped bundle.
 */
export const API_BASE_URL: string =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:3001';

/**
 * The clinician pool's app client id — the `ClinicianClientId` output of the
 * FatigueAuth stack. Public by design: it identifies the client, it does not
 * authenticate it, and the pool client has no secret.
 */
export const COGNITO_CLIENT_ID: string | undefined = import.meta.env['VITE_COGNITO_CLIENT_ID'] as
  | string
  | undefined;

/**
 * True when tokens come from `services/api/local/idp.py` rather than Cognito,
 * which is whenever no Cognito client id was configured at build time.
 *
 * The *session* is not a stand-in either way — it is a real signed token,
 * verified server-side, that expires and refreshes as production's does. What
 * is a stand-in is the thing issuing it, which is why sign-in then offers the
 * seed logins and the top bar carries a badge.
 *
 * `vite.config.ts` refuses a production build without the client id, so this
 * cannot be true in anything Cloudflare Pages deploys.
 */
export const IDP_IS_A_STAND_IN = COGNITO_CLIENT_ID === undefined || COGNITO_CLIENT_ID === '';

/**
 * The logins `services/api/local/seed.py` creates, offered on the sign-in form
 * so that becoming each of them costs a click rather than a trip to the seed
 * output. The password is the same for all of them and is printed by the
 * seeder. Rendered only while `IDP_IS_A_STAND_IN`.
 */
export const SEED_LOGINS: readonly { readonly email: string; readonly label: string }[] = [
  { email: 'rich@example.invalid', label: 'Owner — assigned to nothing, so no caseload' },
  { email: 'clinician-1@example.invalid', label: 'Priya — both cohorts' },
  { email: 'clinician-2@example.invalid', label: 'Tom — evening cohort only' },
  { email: 'owner-b@example.invalid', label: 'The other organisation' },
];

/**
 * Where a patient gets the phone app, shown on `/welcome` once they have
 * chosen a password. Unset until the app is in the stores, and the page says
 * so rather than showing a dead link.
 */
export const IOS_APP_URL: string | undefined =
  (import.meta.env['VITE_IOS_APP_URL'] as string | undefined) || undefined;
export const ANDROID_APP_URL: string | undefined =
  (import.meta.env['VITE_ANDROID_APP_URL'] as string | undefined) || undefined;
